<template>
  <div class="product-prices">
    <b-form-group v-if="isLoggedIn && pricingScheduleOptions && pricingScheduleOptions.length > 1"
                  label="Select your pricing:"
                  label-class="product-prices__pricing-label">
      <b-form-radio-group v-model.number="activePricingId"
                          stacked
                          :options="pricingScheduleOptions"></b-form-radio-group>
    </b-form-group>

    <div v-if="isEarlyBirdPricingSchedule"
         class="early-bird-notice">
      {{ earlyBirdDescription }} price available!
    </div>

    <div class="product-prices__bands">
      <pricing-bands-table v-if="volumePricingBands"
                           :bands="volumePricingBands"></pricing-bands-table>

      <pricing-bands-table v-if="tieredPricingBands"
                           :bands="tieredPricingBands"></pricing-bands-table>
    </div>

    <div v-if="showPricingWidget"
         class="product-prices__content">
      <div v-if="hasPackItems && product.groupHeader">
        <div class="pack-elements">
          <div class="pack-elements__rocker pack-elements__select-all">
            <b-checkbox v-model="selectAllState"
                        title="Select all"
                        size="lg"
                        switch>
            </b-checkbox>
            <span class="pack-elements__text">
              Select all
            </span>
          </div>

          <p class="pack-elements__explanation">
            Select the products
          </p>

          <ul class="pack-elements__list">
            <li v-for="packItem in packItems"
                :key="packItem.productId"
                class="pack-elements__element"
                :class="{
                  'pack-elements__element--inactive': !packItemsState[packItem.productId]
                }">
              <div class="pack-elements__rocker">
                <b-form-checkbox v-model="packItemsState[packItem.productId]"
                                 name="check-button"
                                 size="lg"
                                 :disabled="packItem.disabled"
                                 :title="`Add ${packItem.displayName} to your basket`"
                                 switch>
                </b-form-checkbox>
              </div>

              <div class="pack-elements__text">
                <span class="pack-elements__name">
                  {{ packItem.productDisplayName }}
                </span>

                <p class="pack-elements__description">
                  <sanitize-html :dirty="packItem.description"></sanitize-html>
                </p>
              </div>

              <template v-if="isLoggedIn">
                <div class="pack-elements__options">
                  <div class="pack-elements__price">
                    {{ packItem.packItemDisplayPriceTotal }}
                  </div>
                  <!--<b-select
                  v-if="!priceOnApplication"
                  v-model="packItem.quantity"
                  size="sm"
                  :options="packItem.quantities"
                  :disabled="packItem.defaultOrderQuantityLocked">
                </b-select>-->
                </div>
              </template>

              <div v-else
                   class="pack-elements__price clickable"
                   @click="showPricingModal = true">
                Log in to see prices
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div v-else>
        <div class="product-quantity">
          <div class="product-quantity__col">
            <label class="product-quantity__label">
              {{ quantityLabel }}
            </label>

            <span class="product-quantity__licence">
              {{ getLicenceString(activePrice) }}
            </span>

            <span class="product-quantity__name">
              {{ getProductName(product) }}
            </span>
          </div>

          <div v-if="isLoggedIn"
               class="product-quantity__col">
            <span class="product-quantity__heading">
              <template v-if="!hideQuantity">
                Each
              </template>
            </span>

            <span v-if="!priceOnApplication"
                  class="product-quantity__item-price">
              {{ findAndRenderPriceFromBands(activePrice) }}
            </span>
          </div>

          <div v-if="isLoggedIn && !hideQuantity"
               class="product-quantity__col">
            <span class="product-quantity__heading">
              {{ getQuantityHeader() }}
            </span>

            <b-input v-if="!priceOnApplication"
                     v-model.number="quantity"
                     size="sm"
                     type="number"
                     class="product-quantity__input"
                     :min="minQuantity"
                     :max="maxQuantity"
                     :disabled="defaultOrderQuantityLocked">
            </b-input>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isEarlyBirdPricingSchedule"
         class="early-bird-availability">
      {{ earlyBirdDescription }} price available until {{ earlyBirdDate }}.
      <template v-if="regularPrice">
        &nbsp;Regular price:{{ renderPrice(regularPrice.priceExcVat) }}
      </template>
    </div>

    <div v-if="showPricingMessage"
         class="early-bird-availability">
      {{ getPricingUserMessage() }}
    </div>

    <div v-if="isLoggedIn && (!priceOnApplication && !registerinterest) && !hideBuyNow"
         class="buy-now">
      <AddToBasket v-if="showBuyNowButton"
                   :quantity="quantity"
                   :product-id="product.Id"
                   :stack-size="product.stackSize"
                   v-bind="productOptions"
                   :bulk-products="bulkProducts"
                   class="buy-now__button"
                   @success="onBuyNowClicked"
                   @failure="onAddFailure">
        <template v-if="!showSuccess">
          <span class="buy-now__icon">
            <font-awesome-icon icon="shopping-cart"
                               transform="left-1"></font-awesome-icon>
          </span>

          <span class="buy-now__label">
            Buy now
          </span>
        </template>
        <template v-else>
          <span class="buy-now__icon buy-now__icon--success">
            <font-awesome-icon icon="check"
                               transform="left-1"></font-awesome-icon>
          </span>

          <span class="buy-now__label">
            Added
          </span>
        </template>
      </AddToBasket>

      <div class="buy-now__pricing">
        <div v-if="isEarlyBirdPricingSchedule">
          <font-awesome-icon icon="exclamation-circle"></font-awesome-icon>
          {{ earlyBirdDescription }} price
        </div>

        <div class="buy-now__price">
          <span class="buy-now__currency">
            &pound;
          </span>
          {{ renderTotalPrice() }}
        </div>

        <div class="buy-now__vat">
          Ex-vat
        </div>
      </div>
    </div>

    <div v-else-if="(!priceOnApplication && !registerinterest) && !hideBuyNow"
         class="log-in">
      <button class="btn btn-accent-6 btn-rounded log-in__button"
              @click="showPricingModal = true">
        Log in to see prices
      </button>
    </div>

    <!-- eslint-disable vue/no-v-html-->
    <div v-if="addToBasketError !== null && addToBasketError != undefined
           && addToBasketError.length > 0"
         class="product-prices__add-error"
         v-html="addToBasketError">
    </div>
    <!-- eslint-enable vue/no-v-html-->

    <ProductPricingModal :show="showPricingModal"
                         :register-heading="registerHeading"
                         @close="showPricingModal = false"
                         @login="internalLoggedIn = true">
      <template #login="slotProps">
        <slot name="login"
              v-bind="slotProps"></slot>
      </template>
      <template #register>
        <slot name="register"></slot>
      </template>
    </ProductPricingModal>
    <WishlistModal :show="showWishlistModal"
                   :show-login-prompt="!internalLoggedIn"
                   :product-id="product.productId"
                   :product-format-id="currentPrice.productFormatId"
                   :product-licence-type-id="currentPrice.productLicenceTypeId"
                   :pricing-schedule-id="currentPrice.pricingScheduleId"
                   :bulk-products="bulkProducts"
                   @close="showWishlistModal = false"
                   @login="internalLoggedIn = true">
      <template #login="slotProps">
        <slot name="login"
              v-bind="slotProps"></slot>
      </template>
    </WishlistModal>
    <DeliveryAddressModal :show="showDeliveryAddressModal"
                          :search-type-id="searchTypeId"
                          :order-lines="trackedOrderLineIds"
                          @close="closeDeliveryAddressModal"></DeliveryAddressModal>

    <PriceOnApplicationModal :show="showPriceOnApplicationModal"
                             :is-logged-in="internalLoggedIn"
                             :product-id="product.productId"
                             :pricing-schedule-id="currentPrice.pricingScheduleId"
                             :web-account-id="webAccountId"
                             :org-id="orgId"
                             @close="showPriceOnApplicationModal = false">
    </PriceOnApplicationModal>

    <RegisterInterestModal :show="showRegisterInterestModal"
                           :is-logged-in="internalLoggedIn"
                           :product-id="product.productId"
                           :pricing-schedule-id="currentPrice.pricingScheduleId"
                           :web-account-id="webAccountId"
                           :org-id="orgId"
                           @close="showRegisterInterestModal = false">
    </RegisterInterestModal>
  </div>
</template>

<script>
import { AddToBasket, BasketResource, EcommerceModule } from "spiritweb.packages.ecommerce";
import { format } from "date-fns";
import ProductPricingModal from "~/Components/Product/ProductPricingModal.vue";
import PricingBandsTable from "~/Components/Product/PricingBandsTable.vue";
import WishlistModal from "~/Components/Wishlist/WishlistModal.vue";
import DeliveryAddressModal from "~/Components/Product/DeliveryAddressModal.vue";
import PriceOnApplicationModal from "~/Components/Product/PriceOnApplicationModal.vue";
import SanitizeHtml from "~/Components/Common/SanitizeHtml.vue";
import RegisterInterestModal from "~/Components/Product/RegisterInterestModal.vue";

// Duplicated from ecommerce @/constants.js
// todo: export/import from that package instead
const basket = {
  addToBasket: {
    REQUEST: "sw::addToBasket::request",
    RESPONSE: "sw::addToBasket::response",
    SUCCESS: "sw::addToBasket::success",
    FAILURE: "sw::addToBasket::failure"
  }
};

export default {
  name: "ProductPricing",
  components: {
    ProductPricingModal,
    PricingBandsTable,
    WishlistModal,
    DeliveryAddressModal,
    PriceOnApplicationModal,
    RegisterInterestModal,
    AddToBasket,
    SanitizeHtml
  },
  props: {
    product: {
      type: Object,
      required: true,
      default: () => {
      }
    },
    loggedIn: {
      type: Boolean,
      default: false
    },
    registerHeading: {
      type: String,
      default: "Not got an account?"
    },
    webAccountId: {
      type: String,
      default: null
    },
    searchTypeId: {
      type: String,
      default: null
    },
    orgId: {
      type: String,
      default: null
    },
    hideBuyNow: {
      type: Boolean,
      default: false
    },
    hideQuantity: {
      type: Boolean,
      default: false
    },
    quantityLabel: {
      type: String,
      default: "Select quantity:"
    },
    includeParentPricingSchedules: {
      type: Boolean,
      default: false
    }
  },
  data: () => ( {
    activePricingId: 0,
    packItemsState: {},
    internalLoggedIn: false,
    showPricingModal: false,
    showWishlistModal: false,
    showDeliveryAddressModal: false,
    showPriceOnApplicationModal: false,
    showRegisterInterestModal: false,
    requesting: false,
    quantity: 1,
    trackedOrderLines: [],
    showSuccess: false,
    selectAllState: false,
    packItems: undefined,
    addToBasketError: null
  } ),
  computed: {
    minQuantity () {
      return this.activePrice.minimumOrderQuantity;
    },
    maxQuantity () {
      return this.activePrice.maximumOrderQuantity;
    },
    tieredPricingBands () {
      let result = null;

      if ( this.activePrice ) {
        const bands = this.activePrice.pricingBands;
        if ( bands && bands.key === 0 ) {
          result = bands.value;
        }
      }

      return result;
    },
    volumePricingBands () {
      let result = null;

      if ( this.activePrice ) {
        const bands = this.activePrice.pricingBands;
        if ( bands && bands.key === 1 ) {
          result = bands.value;
        }
      }

      return result;
    },
    pricingSchedules () {
      return this.product.productPrices;
    },
    // user selected pricing schedule
    activePrice () {
      const { activePricingId } = this;
      let result = {};

      if ( this.pricingSchedules && this.pricingSchedules.length > 0 ) {
        result = this.pricingSchedules.find( s => s.id === activePricingId ) || {};
      }
      return result;
    },
    // regular pricing schedule
    regularPrice () {
      let result;

      if ( this.isEarlyBirdPricingSchedule ) {
        try {
          result = this.tryGetRegularPricingSchedule( this.inDatePricingSchedules );
        } catch ( err ) {
          console.error( err );
          result = undefined;
        }
      } else {
        // active price is already the regular pricing
        result = this.activePrice;
      }
      return result;
    },
    // early bird pricing schedule, if any
    earlyBirdPrice () {
      let result = null;

      if ( this.isEarlyBirdPricingSchedule ) {
        result = this.activePrice;
      } else {
        result = this.tryGetEarlyBirdPricingSchedule( this.inDatePricingSchedules );
      }
      return result;
    },
    // pricing schedule to use
    currentPrice () {
      let price = this.activePrice;

      if ( this.earlyBirdPrice ) {
        price = this.earlyBirdPrice;
      }
      return price;
    },
    showPricingWidget () {
      return this.currentPrice && this.currentPrice.purchaseAllowed;
    },
    isLoggedIn () {
      return ( !this.showLoginToSeePricesBtn && !this.internalLoggedIn ) || this.internalLoggedIn;
    },
    quantities () {
      return this.getQuantities( this.currentPrice, this.product.StackSize );
    },
    hasPackItems () {
      let result = false;

      if ( this.packItems && this.packItems.length > 0 ) {
        result = true;
      }
      return result;
    },
    productOptions () {
      return {
        pricingScheduleId: this.activePrice.pricingScheduleId,
        productFormatId: this.activePrice.productFormatId,
        productLicenceTypeId: this.activePrice.productLicenceTypeId
      };
    },
    bulkProducts () {
      let result = null;

      const toAdd = Object.entries( this.packItemsState )
        .filter( ( [, isProductSelected] ) => isProductSelected )
        .map( state => {
          const [id] = state;
          // eslint-disable-next-line
          const product = this.packItems.find(item => item.productId === Number.parseInt(id, 10));
          const { quantity } = product;

          return {
            id,
            quantity,
            options: this.productOptions
          };
        } );

      if ( toAdd && toAdd.length > 0 ) {
        result = toAdd;
      }

      return result;
    },
    isEarlyBirdPricingSchedule () {
      let result = false;

      if ( this.activePrice?.parentPricingScheduleId ) {
        result = true;
      }
      return result;
    },
    earlyBirdDate () {
      let result = null;

      if ( this.earlyBirdPrice ) {
        const date = Date.parse( this.earlyBirdPrice.activeEndDate );
        if ( date ) {
          result = format( date, "dd/MM/yyyy" );
        }
      }
      return result;
    },
    earlyBirdDescription () {
      let result = "Early bird";

      if ( this.earlyBirdPrice && this.earlyBirdPrice.pricingScheduleUserMessage ) {
        result = this.earlyBirdPrice.pricingScheduleUserMessage;
      }
      return result;
    },
    pricingScheduleOptions () {
      let result = [];

      if ( this.hasValidPricingSchedules ) {
        const mutableSchedules = [...this.availablePricingSchedules];
        mutableSchedules.sort( ( a, b ) => a.priorityOrder - b.priorityOrder );
        // Ensure the pricing schedules are sorted by PriorityOrder

        result = mutableSchedules.map( s => {
          const licence = this.getLicenceString( s );
          const { id } = s;
          let label = s.pricingScheduleDescription;

          if ( licence ) {
            label += ` - (${licence})`;
          }

          return {
            value: id,
            text: label
          };
        } );
      }

      return result;
    },
    /**
     * Pricing schedules list filtered only for this parents or children
     * that are in date
     */
    inDatePricingSchedules () {
      let result = null;

      result = this.pricingSchedules.filter( sch => {
        let validDate = false;
        if ( sch.activeStartDate && sch.activeEndDate ) {
          validDate = Date.parse( sch.activeStartDate ) <= Date.now()
            && Date.parse( sch.activeEndDate ) >= Date.now();
        } else {
          validDate = true;
        }

        return validDate ? sch : null;
      } );
      return result || [];
    },
    availablePricingSchedules () {
      return this.getAvailablePricingSchedules( this.pricingSchedules );
    },
    hasValidPricingSchedules () {
      return (
        Array.isArray( this.availablePricingSchedules ) && this.availablePricingSchedules.length > 0
      );
    },
    defaultPricingScheduleId () {
      let id = 0;
      if ( this.hasValidPricingSchedules ) {
        id = this.availablePricingSchedules[0].id;
      }
      return id;
    },
    showBuyNowButton () {
      let result = this.currentPrice.purchaseAllowed;

      if ( this.hasPackItems && result && this.product.groupHeader ) {
        result = this.bulkProducts && this.bulkProducts.length > 0;
      }
      return result;
    },
    showLoginToSeePricesBtn () {
      return this.getPricingMetaData( "website_ShowLoginToSeePrices", this.currentPrice ) === "1";
    },
    showPricingMessage () {
      return this.getPricingMetaData( "website_ShowPricingMessage", this.currentPrice ) === "1";
    },
    priceOnApplication () {
      return this.getPricingMetaData( "website_ShowPriceOnApplication", this.currentPrice ) === "1";
    },
    registerinterest () {
      const showRegisterInterest = this.getPricingMetaData( "website_RegisterInterest", this.currentPrice ) === "1";
      return showRegisterInterest;
    },
    defaultOrderQuantity () {
      return this.getDefaultOrderQuantity( this.currentPrice );
    },
    defaultOrderQuantityLocked () {
      return this.isDefaultOrderQuantityLocked( this.currentPrice );
    },
    trackedOrderLineIds () {
      let result = null;

      if ( this.trackedOrderLines ) {
        result = this.trackedOrderLines
          .map( x => x?.orderLineResult?.orderLineId )
          .filter( id => id != null );
      }

      return result;
    }
  },
  watch: {
    product: {
      immediate: true,
      handler ( val ) {
        this.setPackItems( val );
      }
    },
    currentPrice: {
      immediate: true,
      handler () {
        this.setPackItems( this.product );
        this.$emit( "priceonapplicationchange", this.priceOnApplication );
        this.$emit( "registerinterestchange", this.registerinterest );
        this.$emit( "change", this.activePrice?.pricingScheduleId );
      }
    },
    "currentPrice.purchaseAllowed": {
      immediate: true,
      handler ( val ) {
        this.$emit( "purchase-allowed", val );
        this.$emit( "purchase-allowed", val );
      }
    },
    "currentPrice.productPriceUserMessage": {
      immediate: true,
      handler ( val ) {
        this.$emit( "user-message", val );
      }
    },
    // todo: confirm this is required? Doesn't appear to be utilised.
    hasValidPricingSchedules: {
      immediate: true,
      handler ( val ) {
        this.$emit( "has-valid-pricing-schedule", val );
      }
    },
    showSuccess ( val ) {
      if ( val ) {
        window.setTimeout( () => {
          this.showSuccess = false;
        }, 2000 );
      }
    },
    loggedIn: {
      immediate: true,
      handler ( val ) {
        this.internalLoggedIn = val;
      }
    },
    packItems: {
      deep: true,
      immediate: true,
      handler () {
        this.generatePackItemsState();
      }
    },
    quantities ( val ) {
      this.quantity = this.getQuantityOrDefault( val, this.defaultOrderQuantity );
    },
    selectAllState: {
      immediate: true,
      handler ( val ) {
        this.toggleAllPackItems( val );
      }
    }
  },
  mounted () {
    /**
     * This is a hacky fix for a Chrome bug. This forces the active pricing ID
     * to refresh when the user pressed the back button, causing the correct
     * pricing schedule to be selected.
     */
    const old = this.activePricingId;
    this.activePricingId = 0;
    window.setTimeout( () => {
      this.activePricingId = old;
    }, 50 );
  },
  created () {
    if ( this.defaultPricingScheduleId > 0 ) {
      this.activePricingId = this.defaultPricingScheduleId;
    }

    this.$root.$on( "priceOnApplicationClicked", () => {
      this.showPriceOnApplicationModal = true;
    } );

    this.$root.$on( "addToWishlistClicked", () => {
      this.showWishlistModal = true;
    } );

    this.$root.$on( "addRegisterInterestClicked", () => {
      this.showRegisterInterestModal = true;
    } );
  },
  methods: {
    isPricingScheduleWithinDate ( pricingSchedule ) {
      let result = false;
      const now = Date.now();
      const { activeStartDate, activeEndDate } = pricingSchedule;
      if ( activeStartDate && activeEndDate ) {
        result = Date.parse( activeStartDate ) <= now && Date.parse( activeEndDate ) >= now;
      }
      return result;
    },
    getAvailablePricingSchedules ( pricingSchedules ) {
      return pricingSchedules.reduce( ( result, s ) => {
        const hasChild = !!pricingSchedules
          .slice( 0 ) // mutable copy
          .reduce( ( validChildren, c, i, arr ) => {
            const isChild = s.pricingScheduleId === c.parentPricingScheduleId;
            const licenceMatch = s.productLicenceType === c.productLicenceType;
            const inDate = this.isPricingScheduleWithinDate( c );

            if ( isChild && licenceMatch && inDate ) {
              validChildren.push( c );
              arr.splice( i - 1 ); // make this last iteration
            }
            return validChildren;
          }, [] ).length;

        const childless = !hasChild;
        const hasNoSpecifiedDateRange = s.activeStartDate == null && s.activeEndDate == null;
        const inDate = hasNoSpecifiedDateRange || this.isPricingScheduleWithinDate( s );

        // eslint-disable-next-line
        if ((childless && inDate) || (hasChild && this.includeParentPricingSchedules && inDate)) {
          result.push( s );
        }
        return result;
      }, [] );
    },
    getQuantities ( pricingSchedule, stackSize ) {
      let min = 1;
      let max = 10;

      if ( pricingSchedule ) {
        min = pricingSchedule.minimumOrderQuantity;
        max = pricingSchedule.maximumOrderQuantity;

        if ( !min ) {
          min = 1;
        }

        if ( !max ) {
          max = 10;
        }
      }

      let increment = 1;
      const results = [];

      if ( stackSize ) {
        min = stackSize;
        increment = stackSize;
      }

      let index = 0;
      for ( let i = min; i <= max; i += increment ) {
        results[index] = i;
        index += 1;
      }

      return results;
    },
    getDefaultOrderQuantity ( pricingSchedule ) {
      let quantity = null;

      const metaQuantity = this.getPricingMetaData( "website_DefaultQuantity", pricingSchedule );
      if ( metaQuantity ) {
        quantity = Number.parseInt( metaQuantity, 10 );
      }
      return quantity;
    },
    getQuantityOrDefault ( quantities, defaultOrderQuantity ) {
      let quantity = 1;

      if ( defaultOrderQuantity ) {
        quantity = defaultOrderQuantity;
      } else if ( Array.isArray( quantities ) && quantities.length > 0 ) {
        [quantity] = quantities;
      }
      return quantity;
    },
    isDefaultOrderQuantityLocked ( pricingSchedule ) {
      const isLocked = this.getPricingMetaData( "website_DefaultQuantityLocked", pricingSchedule ) === "1";
      return isLocked;
    },
    setPackItems ( product ) {
      this.packItems = product?.packItems?.map( item => {
        // pricing
        const packItemPricingSchedule = this.getPackItemPricingSchedule( item );
        const packItemPricePerItemExcVat = this.getPackItemPriceExcVat(
          item,
          1,
          packItemPricingSchedule
        );
        // pack item metadata
        const defaultOrderQuantity = this.getDefaultOrderQuantity( packItemPricingSchedule );
        const defaultOrderQuantityLocked = this.isDefaultOrderQuantityLocked(
          packItemPricingSchedule
        );
        // quantities
        const quantities = this.getQuantities( packItemPricePerItemExcVat, item.stackSize );
        const quantity = this.getQuantityOrDefault( quantities, defaultOrderQuantity );
        // ui
        let packItemDisplayPricePerItem = this.renderPrice( 0 );
        let packItemDisplayPriceTotal = this.renderPrice( 0 );
        if ( packItemPricePerItemExcVat ) {
          packItemDisplayPricePerItem = this.renderPrice( packItemPricePerItemExcVat );
          packItemDisplayPriceTotal = this.renderPrice( packItemPricePerItemExcVat * quantity );
        }

        return {
          ...item,
          quantities,
          quantity,
          disabled: packItemPricePerItemExcVat == null,
          packItemDisplayPricePerItem,
          packItemDisplayPriceTotal,
          productDisplayName: this.getProductName( item ),
          defaultOrderQuantityLocked
        };
      } );
    },
    toggleAllPackItems ( state ) {
      if ( this.packItems && this.packItems.length > 0 ) {
        this.packItems.forEach( p => {
          if ( !p.disabled ) {
            this.packItemsState[p.productId] = state;
          }
        } );
      }
    },
    onBuyNowClicked ( product ) {
      this.addToBasketError = null;
      this.showSuccess = true;
      this.trackedOrderLines = this.trackedOrderLines.concat( product.orderLineIds );

      if ( this.product.RequiresDeliveryAddressOnSopLine ) {
        this.showDeliveryAddressModal = true;
      }
    },
    onAddFailure ( error ) {
      if ( error && error.message && error.message !== undefined && error.message.length > 0 ) {
        this.addToBasketError = error.message;
      }
    },
    addToBasket () {
      this.addToBasketError = null;

      if ( Object.values( this.packItemsState ).every( x => x ) ) {
        BasketResource.addProduct( this.product.productId, this.quantity );
      } else {
        const toAdd = Object.entries( this.packItemsState )
          .filter( ( [, value] ) => value )
          .map( x => x[0] );

        this.requesting = true;

        toAdd.forEach( x => {
          const payload = {
            productId: this.product.productId,
            quantity: this.quantity
          };

          this.$root.$emit( basket.addToBasket.REQUEST, payload );
          this.$emit( "request" );

          BasketResource.addProduct( x, this.quantity )
            .then( ( { data } ) => {
              const successPayload = {
                ...payload,
                orderLineId: data
              };

              this.$root.$emit( basket.addToBasket.SUCCESS, successPayload );
              this.$emit( "success", successPayload );
            } )
            .catch( error => {
              this.$root.$emit( basket.addToBasket.FAILURE, error );
              this.$emit( "failure", error );
            } )
            .finally( () => {
              this.$root.$emit( basket.addToBasket.RESPONSE, payload );
              this.$emit( "response", payload );
              this.requesting = false;
            } );
        } );
      }
    },
    findAndRenderPriceFromBands ( price ) {
      let result = price.priceExcVat;

      if ( price.pricingBands && price.pricingBands.value && price.pricingBands.value.length ) {
        const bands = price.pricingBands.value;
        for ( let i = 0; i < bands.length; i += 1 ) {
          const band = bands[i];
          if ( this.quantity >= band.quantityLower
          && ( this.quantity <= band.quantityHigher || band.quantityHigher === -1 ) ) {
            result = band.priceExcVat;
            break;
          }
        }
      }

      return this.renderPrice( result );
    },
    renderPrice ( price ) {
      return EcommerceModule.renderPrice( price, "gbp", "en-gb" );
    },
    getPackItemPriceExcVat ( packItem, quantity = 1, pricingSchedule = null ) {
      let price = null;
      const packItemPricing = pricingSchedule || this.getPackItemPricingSchedule( packItem );

      if ( packItemPricing ) {
        price = null;
        if ( packItemPricing ) {
          price = packItemPricing.priceExcVat;
        }
        if ( quantity > 0 ) {
          price *= quantity;
        }
      }
      return price;
    },
    getPackItemPricingSchedule ( packItem ) {
      const { pricingScheduleId, productLicenceTypeId } = this.currentPrice;
      let pricingSchedule = null;

      if ( packItem && packItem.productPrices ) {
        pricingSchedule = packItem.productPrices.find(
          p => p.pricingScheduleId === pricingScheduleId
            && p.productLicenceTypeId === productLicenceTypeId
        );
      }
      return pricingSchedule;
    },
    renderTotalPrice () {
      let total = null;
      if ( this.hasPackItems ) {
        let value = 0;
        if ( !this.product.groupHeader ) {
          value = this.quantity * this.activePrice.priceExcVat;
        } else {
          this.packItems.forEach( p => {
            if ( this.packItemsState[p.productId] ) {
              value += this.getPackItemPriceExcVat( p, p.quantity );
            }
          } );
        }

        total = this.renderPrice( value ).replace( "£", "" );
      } else {
        const value = this.calculateTotalFromBands( this.activePrice, this.quantity );
        total = this.renderPrice( value ).replace( "£", "" );
      }
      return total;
    },
    calculateTotalFromBands ( price, quantity ) {
      let result = price.priceExcVat * quantity;

      if ( price.pricingBands && price.pricingBands.value && price.pricingBands.value.length ) {
        if ( price.pricingBands.key === 0 ) { // tiered pricing
          result = 0;
          const bands = price.pricingBands.value;
          bands.forEach( b => {
            if ( quantity > b.quantityHigher && b.quantityHigher !== -1 ) {
              result += ( ( b.quantityHigher - b.quantityLower ) + 1 ) * b.priceExcVat;
            } else if ( quantity >= b.quantityLower
              && ( quantity <= b.quantityHigher || b.quantityHigher === -1 ) ) {
              result += ( quantity - ( b.quantityLower - 1 ) ) * b.priceExcVat;
            }
          } );
        } else if ( price.pricingBands.key === 1 ) { // volume pricing
          const bands = price.pricingBands.value;
          bands.every( b => {
            if ( quantity >= b.quantityLower
              && ( quantity <= b.quantityHigher || b.quantityHigher === -1 ) ) {
              result = b.priceExcVat * quantity;
              return false;
            }
            return true;
          } );
        } else {
          console.error( "Unknown pricing band: ", price.pricingBands.key );
        }
      }

      return result;
    },
    tryGetRegularPricingSchedule ( pricingSchedules ) {
      const result = pricingSchedules.find(
        s => s.pricingScheduleId === this.activePrice.parentPricingScheduleId
          && s.productFormatId === this.activePrice.productFormatId
          && s.productLicenceTypeId === this.activePrice.productLicenceTypeId
      );

      if ( !result ) {
        throw new Error( `Failed to find a regular pricing (parent) schedule based on the current early bird (child) pricing schedule:
        ${this.activePrice?.pricingScheduleDescription}
        - ${this.getLicenceString( this.activePrice )}` );
      }
      return result;
    },
    tryGetEarlyBirdPricingSchedule ( pricingSchedules ) {
      return pricingSchedules.find(
        s => s.parentPricingScheduleId === this.activePrice.pricingScheduleId
          && s.productFormatId === this.activePrice.productFormatId
          && s.productLicenceTypeId === this.activePrice.productLicenceTypeId
      );
    },
    generatePackItemsState () {
      if ( this.packItems ) {
        this.packItems.forEach( x => {
          const value = this.packItemsState[x.productId] !== undefined
            ? this.packItemsState[x.productId]
            : false;

          this.$set( this.packItemsState, x.productId, value );
        } );
      }
    },
    getLicenceString ( pricingSchedule ) {
      let result = "";

      if ( pricingSchedule ) {
        if ( pricingSchedule.productFormatId && pricingSchedule.productFormatId !== 0 ) {
          result = pricingSchedule.productFormat;
        }

        if ( pricingSchedule.productLicenceTypeId && pricingSchedule.productLicenceTypeId !== 0 ) {
          // Add a comma if we have a product format string.
          if ( result !== "" ) {
            result += ", ";
          }

          result += `${pricingSchedule.productLicenceType}`;
        }
      }
      return result;
    },
    /* MetaData helpers */
    getPricingMetaData ( fieldName, pricingSchedule ) {
      return this.getObjectMetaData( pricingSchedule, fieldName );
    },
    getProductMetaData ( fieldName ) {
      return this.getObjectMetaData( this.product, fieldName );
    },
    getObjectMetaData ( object, fieldName ) {
      let result = null;

      if ( object && object.metaData ) {
        const metaDataItem = object.metaData[fieldName];

        if ( metaDataItem !== undefined ) {
          result = metaDataItem;
        }
      }

      return result;
    },
    getQuantityHeader () {
      let header = "Quantity";

      const customHeader = this.getProductMetaData( "website_QuantityUnitType" );
      if ( customHeader ) {
        header = customHeader;
      }

      return header;
    },
    getPricingUserMessage () {
      return this.currentPrice.productPriceUserMessage;
    },
    /* End of MetaData helpers */
    showPricing () {
      return ( !this.showLoginToSeePricesBtn && !this.internalLoggedIn ) || this.internalLoggedIn;
    },
    closeDeliveryAddressModal () {
      this.trackedOrderLines = [];
      this.showDeliveryAddressModal = false;
    },
    getProductName ( product ) {
      let result = "Untitled product";

      if ( product?.displayName ) {
        result = product.displayName;
      } else if ( product?.name ) {
        result = product.name;
      }

      return result;
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.product-prices {
  margin-bottom: 30px;

  /deep/ &__pricing-label {
    font-weight: 600;
    color: #008385;
    font-size: 16px;
  }

  &__content {
    background: #e3f3ea;
    padding: 20px;
  }

  &__add-error {
    color: #d0021b;
    font-weight: 500;
    text-align: end;
    margin-top: 5px;
    font-size: 1.2rem;
  }
}

.early-bird-notice {
  background: @color-accent-6;
  padding: 5px 10px;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
}

.early-bird-availability {
  background: @color-accent-6;
  padding: 5px 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.pack-elements {
  font-size: 14px;

  &__explanation {
    font-weight: 600;
  }

  &__list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  &__element {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    opacity: 1;
    transition: opacity 0.1s;

    &:last-child {
      margin-bottom: 0;
    }

    &--inactive {
      opacity: 0.7;
    }
  }

  &__select-all {
    display: flex;
    float: right;
  }

  &__rocker {
    /deep/ .custom-control-input:checked {
      ~ .custom-control-label::before {
        color: #fff;
        border-color: #4fc07d;
        background-color: #4fc07d;
      }
    }

    /deep/ .custom-control-input:not(:disabled) {
      ~ .custom-control-label::before,
      ~ .custom-control-label::after {
        cursor: pointer;
      }

      &:active {
        ~ .custom-control-label::before {
          color: #fff;
          background-color: lighten(#4fc07d, 15%);
          border-color: lighten(#4fc07d, 15%);
        }
      }
    }
  }

  &__text {
    padding: 3px 15px 0 5px;
    flex-grow: 1;
  }

  &__name {
    font-weight: 700;
    margin-bottom: 5px;
  }

  &__description {
    margin-bottom: 0;
  }

  &__price {
    padding: 5px 20px;
    background: #fff;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
  }

  &__options {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
  }
}

.product-quantity {
  display: flex;
  justify-content: space-between;
  font-size: 14px;

  &__input {
    border: 2px solid @color-accent-8;
  }

  &__col {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between;
    align-items: center;
    padding-right: 15px;

    &:first-child {
      flex-grow: 2;
    }

    &:last-child {
      padding-right: 0;
    }
  }

  &__label {
    font-weight: 600;
    color: #007d4f;
    font-size: 16px;
    margin-bottom: 10px;
    margin-top: -5px;
    align-self: flex-start;
  }

  &__licence {
    font-weight: 600;
    font-size: 12px;
    align-self: flex-start;
  }

  &__name {
    font-weight: 600;
    padding-bottom: 5px;
    align-self: flex-start;
  }

  &__heading {
    display: block;
    font-weight: 600;
    margin-bottom: 5px;
    text-align: center;
    font-size: 12px;
    padding-right: 10px;
  }

  &__item-price {
    font-weight: 600;
    padding-bottom: 5px;
  }

  &__line-price {
    background: #fff;
    border-radius: 10px;
    padding: 5px 20px;
    text-align: center;
    font-weight: 600;
    font-size: 13px;
  }
}

.buy-now {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  &__button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: @color-accent-6;
    color: #fff;
    border-radius: 100px 0 0 100px;
    padding: 10px 15px 10px 10px;
    font-size: 1.3rem;
    font-weight: 700;
    text-transform: uppercase;
    border: 0;
    min-width: 170px;
    text-align: center;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background: @color-accent-6-dark;
      color: #ddd;
    }

    &:focus {
      outline: 0;
      box-shadow: 3px 0 7px rgba(0, 0, 0, 0.7) inset;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d0021b;
    background: #fff;
    border-radius: 100px;
    width: 32px;
    height: 32px;
    font-size: 18px;
    margin-right: 10px;

    &--success {
      color: @color-accent-6;
    }
  }

  &__label {
    flex-grow: 1;
    text-align: center;
  }

  &__pricing {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    position: relative;
    background: #ebf5ed;
    border-radius: 0 0 15px 15px;
    margin-right: 20px;
    padding: 5px 20px;
    text-align: center;
    color: @color-accent-6;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    overflow: hidden;
    min-height: 80px;
    min-width: 140px;

    &:before {
      content: "";
      position: absolute;
      top: -1px;
      left: 0;
      right: 0;
      box-shadow: 0 1px 5px 5px fade(desaturate(darken(#d7eddc, 30), 20), 70);
      pointer-events: none;
      height: 1px;
    }
  }

  &__price {
    display: flex;
    align-items: flex-start;
    font-size: 40px;
    font-weight: 900;
    line-height: 34px;
    letter-spacing: -2px;
  }

  &__currency {
    font-weight: 900;
    font-size: 25px;
    line-height: 25px;
    padding-right: 3px;
    padding-top: 3px;
  }
}

.log-in {
  display: flex;
  justify-content: center;
  padding-top: 15px;

  &__button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: @color-accent-6;
    color: #fff;
    border-radius: 100px;
    padding: 10px 45px;
    font-size: 1.3rem;
    font-weight: 700;
    text-transform: uppercase;
    border: 0;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background: @color-accent-6-dark;
      color: #ddd;
    }

    &:focus {
      outline: 0;
      box-shadow: 3px 0 7px rgba(0, 0, 0, 0.7) inset;
    }
  }
}

/deep/ .pricing-bands {
  margin-bottom: 1rem;
}

.clickable {
  cursor: pointer;
}
</style>
